import React, { FC, useState } from 'react';
import { Alert, Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type WebhookSubscriptionSecretDialogProps = {
    open: boolean;
    url: string;
    secret: string;
    onClose: () => void;
};

const WebhookSubscriptionSecretDialog: FC<WebhookSubscriptionSecretDialogProps> = ({
    open,
    url,
    secret,
    onClose,
}: WebhookSubscriptionSecretDialogProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const [copiedField, setCopiedField] = useState<'url' | 'secret' | null>(null);

    const handleCopy = async (value: string, field: 'url' | 'secret') => {
        await navigator.clipboard.writeText(value);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            disableBackdropClick
            maxWidth="sm"
            fullWidth
            title={translate('app-settings:WEBHOOK_SECRET_TITLE')}
            actions={
                <Button onClick={onClose} variant="contained">
                    {translate('app-settings:WEBHOOK_SECRET_DISMISS')}
                </Button>
            }
        >
            <Alert severity="warning" sx={{ mb: 2 }}>
                {translate('app-settings:WEBHOOK_SECRET_WARNING')}
            </Alert>
            <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:WEBHOOK_URL')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <TextField value={url} fullWidth size="small" InputProps={{ readOnly: true }} />
                    <Tooltip title={copiedField === 'url' ? translate('common:COPIED') : translate('common:COPY')}>
                        <IconButton onClick={() => handleCopy(url, 'url')}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:WEBHOOK_SECRET')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <TextField value={secret} fullWidth size="small" InputProps={{ readOnly: true }} />
                    <Tooltip title={copiedField === 'secret' ? translate('common:COPIED') : translate('common:COPY')}>
                        <IconButton onClick={() => handleCopy(secret, 'secret')}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </DHDialog>
    );
};

export default WebhookSubscriptionSecretDialog;
